/**
 * TASK-INT-001: TerrainRenderer - Main Orchestrator
 *
 * Combines all terrain systems (parallax, particles, grid effects) into a single
 * unified system with a single initialization point, update loop, and cleanup method.
 *
 * Container Hierarchy (bottom to top):
 * - Background Container:
 *   - Parallax Background (3 layers: far, mid, near)
 *   - Fog Particles (above parallax)
 * - Effects Container:
 *   - Grid Effects (borders, materialization, spawn effects)
 * - Dust Container:
 *   - Dust Particles (above creatures)
 *
 * Usage:
 * ```typescript
 * const renderer = new TerrainRenderer(theme, gridBounds, hexToPixel, getHexCorners);
 * await renderer.initialize(textures);
 *
 * // Add to scene
 * stage.addChildAt(renderer.getBackgroundContainer(), 0); // Below grid
 * stage.addChildAt(renderer.getEffectsContainer(), 2);    // At grid level
 * stage.addChildAt(renderer.getDustContainer(), 4);       // Above creatures
 *
 * // Update loop
 * app.ticker.add((delta) => {
 *   renderer.update(delta * 16.67); // Convert to ms
 * });
 *
 * // Camera sync
 * camera.on('move', (state) => renderer.updateCameraPosition(state));
 *
 * // Battle events
 * renderer.setPhase(BattlePhase.COMBAT);
 * renderer.onHexHover({ q: 1, r: 2 });
 * renderer.onCreatureSpawned('unit-123', { q: 3, r: 4 });
 *
 * // Cleanup
 * renderer.destroy();
 * ```
 */

import * as PIXI from 'pixi.js';
import type { AxialCoordinate } from '@drawn-of-war/shared';
import type {
  TerrainTheme,
  CameraState,
  BattlePhase,
  ParallaxBackgroundConfig,
  FogConfig,
  DustConfig,
  GridEffectsConfig,
} from '../../types/terrain';
import { ParallaxBackgroundManager } from './parallax-background-manager';
import { ParticleSystemManager } from './particle-system-manager';
import {
  TronGridEffectsManager,
  type DeploymentZone,
} from './tron-grid-effects-manager';

/**
 * Main terrain renderer orchestrating all terrain systems
 */
export class TerrainRenderer {
  // Subsystem managers
  private parallaxManager: ParallaxBackgroundManager;
  private particleManager: ParticleSystemManager;
  private gridEffectsManager: TronGridEffectsManager;

  // Container hierarchy
  private mainContainer: PIXI.Container;
  private backgroundContainer: PIXI.Container;
  private effectsContainer: PIXI.Container;

  // Theme and configuration
  private theme: TerrainTheme;
  private gridBounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };

  /**
   * Create a new TerrainRenderer
   *
   * @param theme - Terrain theme configuration
   * @param gridBounds - Battlefield grid bounds
   * @param hexToPixel - Function to convert hex coordinates to pixel coordinates
   * @param getHexCorners - Function to get the 6 corner points of a hex
   */
  constructor(
    theme: TerrainTheme,
    gridBounds: { minX: number; maxX: number; minY: number; maxY: number },
    hexToPixel: (hex: AxialCoordinate) => { x: number; y: number },
    getHexCorners: (center: { x: number; y: number }) => { x: number; y: number }[]
  ) {
    this.theme = theme;
    this.gridBounds = gridBounds;

    // Create container hierarchy
    this.mainContainer = new PIXI.Container();
    this.backgroundContainer = new PIXI.Container();
    this.effectsContainer = new PIXI.Container();

    this.mainContainer.addChild(this.backgroundContainer);
    this.mainContainer.addChild(this.effectsContainer);

    // Create parallax background configuration
    const parallaxConfig: ParallaxBackgroundConfig = {
      layers: {
        far: {
          textureUrl: theme.assets.farBackground,
          parallaxFactor: theme.parallax.far.factor,
          scale: theme.parallax.far.scale,
          alpha: 1.0,
          useTiling: true, // Far layer uses tiling (placeholder gradient)
        },
        mid: {
          textureUrl: theme.assets.midBackground,
          parallaxFactor: theme.parallax.mid.factor,
          scale: theme.parallax.mid.scale,
          alpha: 1.0,
          useTiling: true, // Mid layer uses tiling (placeholder gradient)
        },
        near: {
          textureUrl: theme.assets.nearBackground,
          parallaxFactor: theme.parallax.near.factor,
          scale: theme.parallax.near.scale,
          alpha: 1.0,
          useTiling: false, // Near layer is a SINGLE image (battlefield-floor.jpg)
          offsetX: theme.parallax.near.offsetX,
          offsetY: theme.parallax.near.offsetY,
        },
      },
      bleedFactor: 3.0,
      gridBounds: gridBounds,
    };

    // Create particle system configurations
    const fogConfig: FogConfig = {
      maxParticles: this.calculateParticleCount(theme.particles.fog.density),
      texture: theme.assets.fogParticle,
      tint: theme.colors.fogTint,
      speed: theme.particles.fog.speed,
      direction: { min: 0, max: Math.PI * 2 }, // All directions
      scale: theme.particles.fog.scale,
      alpha: theme.particles.fog.alpha,
      bounds: {
        x: gridBounds.minX,
        y: gridBounds.minY,
        width: gridBounds.maxX - gridBounds.minX,
        height: gridBounds.maxY - gridBounds.minY,
      },
      wrapEdges: true,
      fadeAtEdges: false,
      respawnAtBottom: false,
    };

    const dustConfig: DustConfig = {
      maxParticles: this.calculateParticleCount(theme.particles.dust.density),
      texture: theme.assets.dustParticle,
      tint: theme.colors.dustTint,
      speed: theme.particles.dust.speed,
      direction: { min: Math.PI * 1.25, max: Math.PI * 1.75 }, // Upward with variance
      scale: theme.particles.dust.scale,
      alpha: theme.particles.dust.alpha,
      bounds: {
        x: gridBounds.minX,
        y: gridBounds.minY,
        width: gridBounds.maxX - gridBounds.minX,
        height: gridBounds.maxY - gridBounds.minY,
      },
      wrapEdges: false,
      fadeAtEdges: true,
      respawnAtBottom: true,
      swayAmplitude: theme.particles.dust.swayAmplitude,
      swayFrequency: theme.particles.dust.swayFrequency,
    };

    // Create grid effects configuration
    const gridConfig: GridEffectsConfig = {
      glowColor: theme.colors.gridGlow,
      glowIntensity: theme.gridEffects.glowIntensity,
      glowPulseSpeed: theme.gridEffects.glowPulseSpeed,
      glowPulseAmount: theme.gridEffects.glowPulseAmount,
      materializeSpeed: theme.gridEffects.materializeSpeed,
      spawnEffectDuration: theme.gridEffects.spawnEffectDuration,
      fadeOutDuration: 500, // Default fade duration
    };

    // Initialize subsystem managers
    this.parallaxManager = new ParallaxBackgroundManager(parallaxConfig);
    this.particleManager = new ParticleSystemManager(fogConfig, dustConfig);
    this.gridEffectsManager = new TronGridEffectsManager(
      gridConfig,
      hexToPixel,
      getHexCorners
    );
  }

  /**
   * Initialize all terrain systems
   *
   * @param textures - Optional map of texture URLs to PIXI.Texture instances
   */
  async initialize(textures?: Map<string, PIXI.Texture>): Promise<void> {
    // Initialize all managers
    await this.parallaxManager.initialize(textures);
    await this.particleManager.initialize();

    // Build container hierarchy
    this.buildContainerHierarchy();
  }

  /**
   * Set deployment zones for grid effects
   * Should be called after initialization with battle-specific zones
   *
   * @param zones - Array of deployment zone definitions
   */
  setDeploymentZones(zones: DeploymentZone[]): void {
    this.gridEffectsManager.initialize(zones);
  }

  /**
   * Update all terrain systems
   * Should be called every frame
   *
   * @param deltaTime - Time elapsed since last frame (seconds)
   */
  update(deltaTime: number): void {
    // Update particles (expects seconds)
    this.particleManager.update(deltaTime);

    // Update grid effects (expects milliseconds, so convert)
    this.gridEffectsManager.update(deltaTime * 1000);

    // Note: Parallax has no update() - only responds to camera movement
  }

  /**
   * Update camera position for all systems
   * Should be called whenever the camera moves or zooms
   *
   * @param camera - Current camera state
   */
  updateCameraPosition(camera: CameraState): void {
    this.parallaxManager.updateCameraPosition(camera);
    this.particleManager.updateCameraPosition(camera);

    // Note: Grid effects are world-space and don't need camera updates
  }

  /**
   * Set the current battle phase
   * Triggers grid fade animation when transitioning to combat
   *
   * @param phase - New battle phase
   */
  setPhase(phase: BattlePhase): void {
    this.gridEffectsManager.setPhase(phase);
  }

  /**
   * Handle hex hover event
   * Triggers materialization effect when hovering over valid hexes
   *
   * @param hex - Hex being hovered, or null if hover ended
   */
  onHexHover(hex: AxialCoordinate | null): void {
    this.gridEffectsManager.onHexHover(hex);
  }

  /**
   * Handle creature spawn event
   * Triggers spawn effect animation
   *
   * @param unitId - ID of the unit being spawned
   * @param hex - Hex where the unit is spawning
   */
  onCreatureSpawned(unitId: string, hex: AxialCoordinate): void {
    this.gridEffectsManager.onCreatureSpawned(unitId, hex);
  }

  /**
   * Get the main container holding all terrain systems
   * For simple integration, add this to the scene
   *
   * @returns Main PIXI.Container
   */
  getContainer(): PIXI.Container {
    return this.mainContainer;
  }

  /**
   * Get the background container (parallax + fog)
   * Should be added to scene below the grid layer
   *
   * @returns Background PIXI.Container
   */
  getBackgroundContainer(): PIXI.Container {
    return this.backgroundContainer;
  }

  /**
   * Get the effects container (grid effects)
   * Should be added to scene at the grid layer level
   *
   * @returns Effects PIXI.Container
   */
  getEffectsContainer(): PIXI.Container {
    return this.effectsContainer;
  }

  /**
   * Get the dust particle container
   * Should be added to scene above creatures
   *
   * @returns Dust PIXI.Container
   */
  getDustContainer(): PIXI.Container {
    return this.particleManager.getDustContainer();
  }

  /**
   * Get the near background sprite (dev tool: background position editor)
   */
  getNearBackgroundSprite(): PIXI.Sprite | null {
    const sprite = this.parallaxManager.getNearSprite();
    if (sprite && !(sprite instanceof PIXI.TilingSprite)) {
      return sprite as PIXI.Sprite;
    }
    return null;
  }

  /**
   * Clean up all resources
   * Should be called when the battlefield is unloaded
   */
  destroy(): void {
    // Destroy all managers
    this.parallaxManager.destroy();
    this.particleManager.destroy();
    this.gridEffectsManager.destroy();

    // Destroy containers
    if (this.mainContainer && !this.mainContainer.destroyed) {
      this.mainContainer.destroy({ children: true });
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Build the container hierarchy
   * Organizes all subsystem containers into the correct layer order
   */
  private buildContainerHierarchy(): void {
    // Background container (bottom to top):
    // 1. Parallax layers (far, mid, near)
    this.backgroundContainer.addChild(this.parallaxManager.getContainer());

    // 2. Fog particles (above parallax)
    this.backgroundContainer.addChild(this.particleManager.getFogContainer());

    // Effects container:
    // 3. Grid effects (borders, materialization, spawn effects)
    this.effectsContainer.addChild(this.gridEffectsManager.getContainer());

    // Dust is separate - returned via getDustContainer() for manual placement above creatures
  }

  /**
   * Calculate particle count based on density and grid bounds
   *
   * @param density - Particles per 1000x1000 px area
   * @returns Number of particles to create
   */
  private calculateParticleCount(density: number): number {
    const area =
      (this.gridBounds.maxX - this.gridBounds.minX) *
      (this.gridBounds.maxY - this.gridBounds.minY);

    // Density is per 1000x1000 px = 1,000,000 px²
    const baseArea = 1000 * 1000;

    return Math.ceil((area / baseArea) * density);
  }
}
